import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import type { StageToolbar } from '../../stage-toolbar';
import { useScaleController } from './use-scale-controller';

export const InlineScale = ({ toolbar }: { toolbar: StageToolbar }) => {
    const { $t } = useLocaleStore();
    const { framework, max, min, scale, setScale, zoomIn, zoomOut } = useScaleController(toolbar);
    const [pointerInside, setPointerInside] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [focused, setFocused] = useState(false);
    const [inputActive, setInputActive] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const displayScale = String(Math.round(scale * 100));

    useEffect(() => {
        const blur = () => {
            if (inputRef.current && focused) inputRef.current.blur();
        };
        const zoomListener = framework.service.edgelessCanvas.onZoom(blur);
        const panListener = framework.service.edgelessCanvas.onPan(blur);
        return () => {
            framework.service.events.off(zoomListener);
            framework.service.events.off(panListener);
        };
    }, [focused, framework]);

    return (
        <div
            className={classNames('stage-toolbar-scale-inline', {
                'stage-toolbar-scale-inline--expanded': pointerInside,
            })}
            onMouseEnter={() => setPointerInside(true)}
            onMouseLeave={() => setPointerInside(false)}
        >
            <Tooltip
                placement="top"
                title={
                    scale <= min
                        ? $t((value) => value.navigatorLayer.minCanvas)
                        : $t((value) => value.navigatorLayer.shrink)
                }
                trigger="hover"
            >
                <button
                    type="button"
                    className="stage-toolbar-scale-inline__button"
                    aria-hidden={!pointerInside}
                    tabIndex={-1}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                        zoomOut();
                    }}
                >
                    <IconFont name="ZoomOutBold" fontSize={16} />
                </button>
            </Tooltip>
            <div
                className={classNames('stage-toolbar-scale-inline__input', {
                    'stage-toolbar-scale-inline__input--inactive': !inputActive,
                    'stage-toolbar-scale-inline__input--focused': focused,
                })}
                onMouseEnter={() => setInputActive(true)}
                onMouseLeave={() => {
                    setInputActive(false);
                    if (inputRef.current && focused) inputRef.current.blur();
                }}
            >
                <input
                    ref={inputRef}
                    type="text"
                    value={focused ? inputValue : displayScale}
                    tabIndex={inputActive ? 0 : -1}
                    readOnly={!inputActive}
                    onFocus={() => {
                        setFocused(true);
                        setInputValue(displayScale);
                    }}
                    onChange={(event) => {
                        if (/^\d{0,3}$/.test(event.target.value)) setInputValue(event.target.value);
                    }}
                    onBlur={(event) => {
                        setFocused(false);
                        const value = Number(event.target.value);
                        if (Number.isNaN(value)) return;
                        const ratio = Math.max(Math.min(value, max * 100), 1);
                        setScale(ratio / 100);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                />
                <span>%</span>
            </div>
            <Tooltip
                placement="top"
                title={
                    scale >= max
                        ? $t((value) => value.navigatorLayer.maxCanvas)
                        : $t((value) => value.navigatorLayer.enlarge)
                }
                trigger="hover"
            >
                <button
                    type="button"
                    className="stage-toolbar-scale-inline__button"
                    aria-hidden={!pointerInside}
                    tabIndex={-1}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                        zoomIn();
                    }}
                >
                    <IconFont name="ZoomInBold" fontSize={16} />
                </button>
            </Tooltip>
        </div>
    );
};
